import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AlertTriangle, CheckCircle, XCircle, Eye, Clock, MessageSquare, Flag, TrendingUp } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModerationDashboard = () => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [statusFilter]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await axios.get(`${API}/moderation/reports${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/moderation/stats`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleUpdateReport = async (reportId, status, action, notes) => {
    try {
      const moderatorId = localStorage.getItem('adminUserId');
      const moderatorName = localStorage.getItem('adminUsername');

      await axios.put(`${API}/moderation/reports/${reportId}?moderator_id=${moderatorId}`, {
        status,
        action_taken: action,
        moderator_notes: notes
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
      });

      // Log moderation action
      if (action) {
        await axios.post(`${API}/moderation/actions?moderator_id=${moderatorId}&moderator_name=${moderatorName}`, {
          action_type: action,
          target_type: selectedReport.content_type,
          target_id: selectedReport.content_id,
          reason: selectedReport.reason,
          notes: notes,
          report_id: reportId
        }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
      }

      toast.success('Report updated successfully');
      setSelectedReport(null);
      fetchReports();
      fetchStats();
    } catch (error) {
      console.error('Error updating report:', error);
      toast.error('Failed to update report');
    }
  };

  const getReasonBadge = (reason) => {
    const badges = {
      harassment: { color: 'bg-red-100 text-red-800', icon: '🚫' },
      hate_speech: { color: 'bg-orange-100 text-orange-800', icon: '⚠️' },
      misinformation: { color: 'bg-yellow-100 text-yellow-800', icon: '❌' },
      spam: { color: 'bg-blue-100 text-blue-800', icon: '📧' },
      illegal: { color: 'bg-purple-100 text-purple-800', icon: '🚨' },
      self_harm: { color: 'bg-pink-100 text-pink-800', icon: '💔' },
      other: { color: 'bg-gray-100 text-gray-800', icon: '🔍' }
    };
    const badge = badges[reason] || badges.other;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.icon} {reason.replace('_', ' ')}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      reviewed: { color: 'bg-blue-100 text-blue-800', icon: Eye },
      resolved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      dismissed: { color: 'bg-gray-100 text-gray-800', icon: XCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        <Icon className="w-3 h-3" /> {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black text-ssdp-navy font-heading">
            Moderation Dashboard
          </h1>
          <p className="text-gray-600 font-body">
            Review and manage content reports
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700 font-semibold">Pending</p>
                <p className="text-2xl font-black text-yellow-900">{stats.reports_by_status?.pending || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-semibold">Reviewed</p>
                <p className="text-2xl font-black text-blue-900">{stats.reports_by_status?.reviewed || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-green-50 border-2 border-green-300 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-semibold">Resolved</p>
                <p className="text-2xl font-black text-green-900">{stats.reports_by_status?.resolved || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>

          <div className="bg-purple-50 border-2 border-purple-300 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-700 font-semibold">Total Actions</p>
                <p className="text-2xl font-black text-purple-900">{stats.total_moderation_actions || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 border-2 border-ssdp-navy">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reports</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="reviewed">Reviewed</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="dismissed">Dismissed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <div className="bg-white border-3 border-ssdp-navy rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-ssdp-navy text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-black">Date</th>
                <th className="px-4 py-3 text-left text-sm font-black">Reason</th>
                <th className="px-4 py-3 text-left text-sm font-black">Content Type</th>
                <th className="px-4 py-3 text-left text-sm font-black">Status</th>
                <th className="px-4 py-3 text-left text-sm font-black">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    Loading reports...
                  </td>
                </tr>
              ) : reports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    No reports found
                  </td>
                </tr>
              ) : (
                reports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {formatDate(report.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      {getReasonBadge(report.reason)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {report.content_type}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(report.status)}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        onClick={() => setSelectedReport(report)}
                        size="sm"
                        className="bg-ssdp-teal text-white hover:bg-ssdp-teal-hover"
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Report Detail Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border-3 border-ssdp-navy"
          >
            <h3 className="text-2xl font-black text-ssdp-navy mb-4">Report Details</h3>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Reason</label>
                  <div className="mt-1">{getReasonBadge(selectedReport.reason)}</div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedReport.status)}</div>
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Content Type</label>
                <p className="text-gray-800 mt-1">{selectedReport.content_type}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Content ID</label>
                <p className="text-gray-800 mt-1 font-mono text-sm">{selectedReport.content_id}</p>
              </div>

              {selectedReport.description && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Reporter's Description</label>
                  <p className="text-gray-800 mt-1 p-3 bg-gray-50 rounded-lg">{selectedReport.description}</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => handleUpdateReport(selectedReport.id, 'resolved', 'content_removed', 'Content removed after review')}
                  className="flex-1 bg-red-600 text-white hover:bg-red-700"
                >
                  Remove Content
                </Button>
                <Button
                  onClick={() => handleUpdateReport(selectedReport.id, 'dismissed', 'no_action', 'No violation found')}
                  className="flex-1 bg-gray-600 text-white hover:bg-gray-700"
                >
                  Dismiss
                </Button>
                <Button
                  onClick={() => setSelectedReport(null)}
                  variant="outline"
                  className="border-2 border-ssdp-navy"
                >
                  Close
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ModerationDashboard;
